import { useCallback, useEffect, useMemo, type JSX } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { trackGameClick } from '@comeon/analytics';
import { Caption, H4Bold, Spacer } from '@comeon/ui';

import { fetchAllGames } from '~/components/casino-shared/all-games/ducks';
import { LIST_NAME } from '~/components/casino-shared/constants';
import CategoryCarousel from '~/components/casino-shared/explore/categories/category-carousel';
import gridTemplates from '~/components/casino-shared/explore/grids';
import useExploreData from '~/components/casino-shared/explore/use-explore-data';
import GameCard from '~/components/casino-shared/game-card/game-card';
import { EnhancedGame, type TrackGameClickParamsFromCategory } from '~/components/casino-shared/types';
import {
	casinoExploreRecommendationSelector,
	liveCasinoExploreRecommendationSelector
} from '~/components/search/ducks/selectors';
import {
	gamesByProviderCodeSelector,
	gamesBySubProviderCodeSelector
} from '~/components/casino-shared/ducks/selectors';
import type { TranslationHash } from '~/components/translation/use-translations';
import useCasinoLocation from '~/hooks/use-casino-location';

const RECOMMENDED_GAMES_IN_ROW = 5;

const PromotionTitle = styled(H4Bold)`
	color: var(--game-info-row-value-text-color);
`;

const PromotionDescription = styled(Caption)`
	margin-bottom: 12px;
	color: rgba(var(--font-supportive), 1);
`;

type PromotionSectionCarouselProps = {
	title: string;
	description: string;
	games: JSX.Element[];
};

const PromotionSectionCarousel = ({ title, description, games }: PromotionSectionCarouselProps) => {
	const { GridComponent } = gridTemplates[0];

	if (!games.length) {
		return null;
	}

	const carouselWidth = games.length * 104 + (games.length - 1) * 12;

	return (
		<>
			<PromotionTitle>{title}</PromotionTitle>
			<PromotionDescription>{description}</PromotionDescription>
			<CategoryCarousel $scrollerPadding="0" disableEndGap childrenWidth={carouselWidth}>
				<GridComponent GameCards={games} itemDisplayType="xs" categoryType="gameInfoProviderGames" />
			</CategoryCarousel>
			<Spacer height={12} />
		</>
	);
};

const PromotionSection = ({ game, t }: { game: EnhancedGame; t: TranslationHash }) => {
	const { isLiveCasinoLocation } = useCasinoLocation();
	const dispatch = useDispatch() as (action: unknown) => void;

	// Remove when custom recommendations are available
	useExploreData();

	useEffect(() => {
		dispatch(fetchAllGames());
	}, [dispatch]);

	const handleGameClick = useCallback(
		(trackingData: TrackGameClickParamsFromCategory) => {
			trackGameClick({
				...trackingData,
				listName: LIST_NAME.GAME_INFO,
				isLiveCasinoLocation
			});
		},
		[isLiveCasinoLocation]
	);

	const casinoExploreRecommendations = useSelector(casinoExploreRecommendationSelector);
	const liveCasinoExploreRecommendations = useSelector(liveCasinoExploreRecommendationSelector);

	const similarGameCards = useMemo(() => {
		const recommendations = isLiveCasinoLocation ? liveCasinoExploreRecommendations : casinoExploreRecommendations;

		return recommendations?.reduce<JSX.Element[]>((acc, recommendedGame, index) => {
			if (game.code === recommendedGame.code) {
				return acc;
			}

			return [
				...acc,
				<GameCard
					bodyScrollLockIgnore
					disableHover
					disableInfo
					disableTags
					disableBottom
					game={recommendedGame}
					index={index}
					key={recommendedGame.code}
					listName={LIST_NAME.GAME_INFO}
					loading="lazy"
					showJackpotTicker={false}
					trackGameClick={handleGameClick}
				/>
			];
		}, []);
	}, [
		casinoExploreRecommendations,
		game.code,
		handleGameClick,
		isLiveCasinoLocation,
		liveCasinoExploreRecommendations
	]);

	const gamesByProviderCode = useSelector(gamesByProviderCodeSelector);
	const gamesBySubProviderCode = useSelector(gamesBySubProviderCodeSelector);

	const providerGameCards = useMemo(() => {
		const subProviderGames = gamesBySubProviderCode[game.casinoSubProviderVO?.code ?? ''] ?? [];
		const providerGames = gamesByProviderCode[game.provider.code] ?? [];

		return [...subProviderGames, ...providerGames]?.reduce<JSX.Element[]>((acc, providerGame, index) => {
			if (
				providerGame.categoryCode !== game.categoryCode ||
				acc.length >= RECOMMENDED_GAMES_IN_ROW ||
				game.code === providerGame.code
			) {
				return acc;
			}

			return [
				...acc,
				<GameCard
					bodyScrollLockIgnore
					disableHover
					disableInfo
					disableTags
					disableBottom
					game={providerGame}
					index={index}
					key={providerGame.code}
					listName={LIST_NAME.GAME_INFO}
					loading="lazy"
					showJackpotTicker={false}
					trackGameClick={handleGameClick}
				/>
			];
		}, []);
	}, [
		game.casinoSubProviderVO,
		game.categoryCode,
		game.code,
		game.provider.code,
		gamesByProviderCode,
		gamesBySubProviderCode,
		handleGameClick
	]);

	return (
		<>
			<PromotionSectionCarousel
				title={t.GAME_INFO_SIMILAR_GAMES_TITLE}
				description={t.GAME_INFO_SIMILAR_GAMES_DESCRIPTION}
				games={similarGameCards}
			/>
			<PromotionSectionCarousel
				title={t.GAME_INFO_PROVIDER_GAMES_TITLE.replace(
					/%s/g,
					game.casinoSubProviderVO?.name || game.provider.name
				)}
				description={t.GAME_INFO_PROVIDER_GAMES_DESCRIPTION}
				games={providerGameCards}
			/>
		</>
	);
};

export default PromotionSection;
